import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { FiUploadCloud } from 'react-icons/fi';
import { ImSpinner8 } from 'react-icons/im';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { commonToast } from '../helper/Toast';

export default function Contentcreator() {
    const [uploading, setUploading] = useState(false);
    const [responseData, setResponseData] = useState([
        {
            "question": "What is a closure in JavaScript?",
            "answer": "A closure is a function that retains access to its lexical scope, even when the function is executed outside that scope."
        },
        {
            "question": "What is hoisting in JavaScript?",
            "answer": "Hoisting is a JavaScript mechanism where variables and function declarations are moved to the top of their containing scope during the compile phase."
        },
        {
            "question": "What is the Temporal Dead Zone (TDZ)?",
            "answer": "The Temporal Dead Zone is the period of time from the start of a block until a variable is declared, where the variable cannot be accessed."
        },
        {
            "question": "How do Promises differ from async/await in JavaScript?",
            "answer": "Promises are objects that represent the eventual completion (or failure) of an asynchronous operation, while async/await is syntactic sugar built on top of Promises that allows for writing asynchronous code in a synchronous style."
        },
        {
            "question": "What is the difference between shallow copy and deep copy?",
            "answer": "A shallow copy creates a new object but copies references to the nested objects, while a deep copy creates a new object and recursively copies all nested objects."
        },
        {
            "question": "What is the call stack in JavaScript?",
            "answer": "The call stack is a data structure that keeps track of function calls in a program, managing the execution order of functions."
        },
        {
            "question": "What is the purpose of the IntersectionObserver API?",
            "answer": "The IntersectionObserver API allows developers to asynchronously observe changes in the intersection of a target element with an ancestor element or with a top-level document's viewport."
        },
        {
            "question": "What is the use of localStorage in web APIs?",
            "answer": "localStorage is a web API that allows storing key-value pairs in a web browser with no expiration time, making data persistent across sessions."
        },
        {
            "question": "What is the main advantage of using arrow functions in JavaScript?",
            "answer": "Arrow functions provide a concise syntax and also lexically bind the `this` value, avoiding issues with context in nested functions."
        },
        {
            "question": "What is a factory design pattern?",
            "answer": "The factory design pattern is a creational design pattern that uses a factory method to create objects without specifying the exact class of object that will be created."
        },
        {
            "question": "What is the purpose of the Context API in React?",
            "answer": "The Context API allows for the sharing of values (like state) across the component tree without passing props manually at every level."
        },
        {
            "question": "What does the useEffect hook do in React?",
            "answer": "The useEffect hook allows you to perform side effects in functional components, such as data fetching, subscriptions, or manually changing the DOM."
        },
        {
            "question": "What is the difference between controlled and uncontrolled components in React?",
            "answer": "Controlled components are form elements whose values are controlled by React state, while uncontrolled components store their own state internally and are accessed via refs."
        },
        {
            "question": "What is memoization?",
            "answer": "Memoization is an optimization technique that caches the results of function calls and returns the cached result when the same inputs occur again."
        },
        {
            "question": "What is the purpose of the Notification API?",
            "answer": "The Notification API allows web applications to display notifications to the user, even when the web page is not in focus."
        },
        {
            "question": "What are Generators in JavaScript?",
            "answer": "Generators are special functions that can be paused and resumed, allowing them to yield multiple values over time, which can be iterated over."
        },
        {
            "question": "What is React Suspense used for?",
            "answer": "React Suspense is used to handle loading states in React applications, allowing components to wait for some condition (like data fetching) before rendering."
        },
        {
            "question": "What does the spread operator do in JavaScript?",
            "answer": "The spread operator allows an iterable (like an array or object) to be expanded in places where zero or more arguments or elements are expected, such as in function calls or array literals."
        },
        {
            "question": "What is the purpose of custom hooks in React?",
            "answer": "Custom hooks allow developers to extract and reuse stateful logic across components, promoting code reuse and separation of concerns."
        },
        {
            "question": "What is server-side rendering (SSR) in React?",
            "answer": "Server-side rendering (SSR) is the process of rendering React components on the server and sending the fully rendered page to the client, improving performance and SEO."
        }
    ]);
    const [uploadedFile, setUploadedFile] = useState(null);
    const token = JSON.parse(localStorage?.getItem("zubrus_user_data"))?.data?.tokens?.accessToken;

    const onDrop = useCallback(async (acceptedFiles) => {
        const pdf = acceptedFiles[0];
        if (!pdf) return;

        setUploadedFile(pdf);
        const formData = new FormData();
        formData.append("file", pdf);

        try {
            setUploading(true);
            const response = await axios.post(`${process.env.REACT_APP_AI_BASEURL}/quiz`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`,
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
                    'Access-Control-Allow-Headers': 'Content-Type, Authorization'
                },
                withCredentials: true
            });
            setResponseData(response.data);
            commonToast('File uploaded successfully', "success");
        } catch (error) {
            console.error("Upload failed:", error);
            if (error.response) {
                commonToast(error.response.data.message, "error");
            } else if (error.request) {
                commonToast(error?.message, "error");
            } else {
                commonToast('Error uploading file', "error");
            }
        } finally {
            setUploading(false);
        }
    }, [token]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/pdf': ['.pdf'] },
        multiple: false
    });

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Content Creator</h1>
                    <p className="text-gray-600">Upload your PDF to generate interactive content</p>
                </div>

                <div className="bg-white rounded-2xl shadow-lg p-8">
                    <div
                        {...getRootProps()}
                        className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all duration-200 ease-in-out cursor-pointer
                            ${isDragActive
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}
                    >
                        <input {...getInputProps()} />
                        <div className="space-y-4">
                            <div className="flex justify-center">
                                <FiUploadCloud className={`w-12 h-12 ${isDragActive ? 'text-blue-500' : 'text-gray-400'}`} />
                            </div>
                            <div className="text-gray-600">
                                {isDragActive ? (
                                    <p className="text-blue-500 font-medium">Drop your PDF here</p>
                                ) : (
                                    <div>
                                        <p className="font-medium">Drag & drop your PDF here</p>
                                        <p className="text-sm text-gray-500 mt-1">or click to browse files</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {uploadedFile && (
                        <div className="mt-6 p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <IoDocumentTextOutline className="w-8 h-8 text-blue-500" />
                                <div>
                                    <p className="font-medium text-gray-900 break-all">{uploadedFile.name}</p>
                                    <p className="text-sm text-gray-500">{formatFileSize(uploadedFile.size)}</p>
                                </div>
                            </div>
                            {uploading && (
                                <ImSpinner8 className="w-6 h-6 text-blue-500 animate-spin" />
                            )}
                        </div>
                    )}

                    {responseData && (
                        <div className="mt-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Generated Quiz Questions</h3>
                            <div className="grid grid-cols-1 gap-6">
                                {responseData.map((item, index) => (
                                    <div
                                        key={index}
                                        className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-100"
                                    >
                                        <div className="p-6">
                                            <div className="flex items-start sm:items-center sm:justify-center gap-4">
                                                <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                                                    <span className="text-white font-semibold text-lg">{index + 1}</span>
                                                </div>
                                                <div className="flex-1">
                                                    <h4 className="text-lg font-semibold text-gray-900 mb-3 sm:mb-0 group-hover:text-blue-600 transition-colors duration-200">
                                                        {item.question}
                                                    </h4>
                                                    <div className="bg-gray-50 sm:hidden rounded-lg p-4 border border-gray-100">
                                                        <div className="flex items-start gap-3">
                                                            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                                                                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            </div>
                                                            <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>
                                            <div className="bg-gray-50 sm:mt-5 sm:flex hidden rounded-lg p-4 border border-gray-100">
                                                <div className="flex items-start gap-3">
                                                    <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                                                        <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    </div>
                                                    <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {uploading && (
                    <div className="mt-6 text-center">
                        <div className="inline-flex items-center space-x-2 text-blue-600">
                            <ImSpinner8 className="w-5 h-5 animate-spin" />
                            <span>Processing your PDF...</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
